import React, { useState } from 'react';
import { Mail, CheckCircle, Bell } from 'lucide-react';
import Container from '../common/Container';

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate email
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    // Reset error state
    setError('');
    setSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      // Here you would typically send this to your API
      console.log('Newsletter signup:', email);
      
      // Reset form and show success
      setEmail('');
      setSubmitting(false);
      setSubscribed(true);
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setSubscribed(false);
      }, 5000);
    }, 1000);
  };

  return (
    <section className="py-12 bg-gradient-to-b from-white to-slate-50 relative overflow-hidden" aria-labelledby="newsletter-heading">
      {/* Static background elements */}
      <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-br from-cyan-50 to-transparent rounded-bl-full opacity-70 transform translate-x-1/4 -translate-y-1/4"></div>
      <div className="absolute bottom-0 left-0 w-1/2 h-1/2 bg-gradient-to-tl from-blue-50 to-transparent rounded-tr-full opacity-70 transform -translate-x-1/4 translate-y-1/4"></div>
      
      <Container>
        <div className="max-w-4xl mx-auto bg-gradient-to-r from-cyan-50 via-blue-50 to-blue-100 rounded-2xl shadow-lg p-6 sm:p-8 relative overflow-hidden">
          {/* Static decoration */}
          <div className="absolute -top-8 -right-8 w-32 h-32 bg-gradient-to-br from-cyan-200/20 to-blue-200/20 rounded-full blur-xl"></div>
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-gradient-to-tr from-blue-200/20 to-cyan-200/20 rounded-full blur-xl"></div>
          
          <div className="text-center mb-6 relative">
            <div className="w-14 h-14 rounded-full bg-gradient-to-r from-cyan-600 to-blue-700 flex items-center justify-center text-white mx-auto mb-4 shadow-lg">
              <Mail className="w-7 h-7" aria-hidden="true" />
            </div>
            <h2 id="newsletter-heading" className="text-2xl sm:text-3xl font-bold mb-3 text-slate-900">
              Stay Updated with <span className="bg-clip-text text-transparent bg-gradient-to-r from-cyan-600 to-blue-700">Industry Trends</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto text-base sm:text-lg">
              Subscribe to our newsletter for the latest automation insights, case studies, and technology updates
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="max-w-xl mx-auto relative">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className={`flex-1 px-4 py-3 rounded-full border-0 focus:ring-2 focus:ring-cyan-500 shadow-md bg-white ${
                  error ? 'ring-2 ring-red-500' : ''
                }`}
                aria-label="Email address for newsletter"
                aria-describedby={error ? "newsletter-error" : undefined}
                disabled={submitting || subscribed}
                required
              />
              <button
                type="submit"
                className={`px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-700 text-white font-medium rounded-full shadow-md ${
                  submitting ? 'opacity-70 cursor-not-allowed' : ''
                } ${subscribed ? 'bg-green-500' : ''}`}
                disabled={submitting || subscribed}
              >
                {submitting ? (
                  <span className="flex items-center justify-center">
                    <svg className="-ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Subscribing...
                  </span>
                ) : subscribed ? (
                  <span className="flex items-center justify-center">
                    <CheckCircle className="w-4 h-4 mr-1" />
                    Subscribed!
                  </span>
                ) : (
                  'Subscribe'
                )}
              </button>
            </div>
            
            {error && (
              <div id="newsletter-error" className="text-red-600 text-sm mt-2 ml-4 flex items-center">
                <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"></path>
                </svg>
                {error}
              </div>
            )}
            
            {subscribed && (
              <div className="text-green-600 text-sm mt-2 ml-4 flex items-center">
                <CheckCircle className="w-4 h-4 mr-1" aria-hidden="true" />
                Thank you for subscribing to our newsletter!
              </div>
            )}
            
            <div className="text-center mt-4 text-sm text-slate-500 flex items-center justify-center">
              <div className="flex items-center mr-4">
                <CheckCircle className="w-4 h-4 mr-1 text-cyan-600" aria-hidden="true" />
                No spam, ever
              </div>
              <div className="flex items-center">
                <Bell className="w-4 h-4 mr-1 text-cyan-600" aria-hidden="true" />
                Monthly updates only
              </div>
            </div>
          </form>
        </div>
      </Container>
    </section>
  );
};

export default Newsletter;